import moment from "moment-timezone";
import { TYPO } from "styles/Typo";
import { useBriefingDate } from "utils/clientAPIs/discuss";
import { useEffect, useRef, useState } from "react";
import BriefingFloating from "./BriefingFloating";
import SkeletonBlock from "components/skeleton";
import { Forward } from "components/common/Icons";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { TAILWIND_TO_CSS } from "utils/constant/STYLE";
import { useTrackedNavigation } from "lib/analytics/react/hooks";
import { TrackedLink } from "lib/analytics/react/link";

function BriefingAd({ type = "main" }: { type?: "main" | "discuss" }) {
  const [briefingDate, isBriefingDateLoading] = useBriefingDate();
  const { navigateWithTracking } = useTrackedNavigation();
  const [viewed, setViewed] = useState(false);
  const [adVisible, setAdVisible] = useState(true);
  const adRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const { t } = useTranslation("main");
  const briefingDateLabel =
    router.locale === "ko"
      ? moment(briefingDate).format("M월 D일")
      : moment(briefingDate).format("MMM D");
  useEffect(() => {
    if (!briefingDate) return;
    const viewedDate = localStorage.getItem("briefingDate");
    if (viewedDate && briefingDate === viewedDate) {
      setViewed(true);
    }
  }, [briefingDate]);

  useEffect(() => {
    if (!briefingDate || !adRef.current) return;
    if (type === "discuss") return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setAdVisible(true);
        } else {
          setAdVisible(false);
        }
      });
    });
    observer.observe(adRef.current);

    return () => {
      if (adRef.current) {
        observer.unobserve(adRef.current);
      }
    };
  }, [briefingDate, adRef.current]);

  if (isBriefingDateLoading)
    return (
      <div className="px-5 pt-4">
        <SkeletonBlock width={"100%"} height={80} />
      </div>
    );
  return (
    <div className="px-6 pt-6" ref={adRef}>
      <div
        className={`bg-gray2 rounded-[20px] flex flex-col border border-gray5 ${
          type === "discuss" ? "cursor-pointer" : ""
        }`}
        onClick={() => {
          if (type === "discuss") {
            navigateWithTracking({
              href: "/briefing?from=discuss",
              itemspec: {
                kind: "section_nav",
                section_id: "report_briefing",
                nav_key: "report_navbtn_go_briefing",
              },
            });
          }

          return;
        }}
        data-gtm-id={type === "discuss" ? "report-briefing-ad-button" : ""}
      >
        <div className="flex items-center px-5 py-[10px] gap-3">
          <div>
            {viewed ? (
              <div className="flex items-center justify-center p-[3px] w-[30px] h-[30px] rounded-[8px] bg-blue10">
                <CheckedIcon />
              </div>
            ) : (
              <div className="flex items-center justify-center p-[3px] w-[30px] h-[30px] rounded-[8px] bg-gray80">
                <NewBriefingIcon />
              </div>
            )}
          </div>
          <div className="flex flex-col grow">
            <p className={`text-gray70 ${TYPO.TYPO7_1_RG}`}>
              {t("briefingLabel", { date: briefingDateLabel })}
            </p>
            <p
              className={` ${TYPO.TYPO5_1_SB} ${
                !viewed ? "text-blue40" : "text-gray80"
              }`}
            >
              {!viewed ? t("briefingCta") : t("briefingDone")}
            </p>
          </div>
          {type === "discuss" && (
            <Forward fill="gray60" height={12} width={12} />
          )}
        </div>
        {type === "main" && (
          <TrackedLink
            asTrace={true}
            itemspec={{
              kind: "section_nav",
              section_id: "feed_briefing",
              nav_key: "feed_navbtn_go_briefing",
            }}
            href={`/briefing`}
            className=" gap-2 flex justify-between border-t border-gray15  items-center px-5 py-[10px] w-full"
            data-gtm-id="briefing-ad-button"
          >
            <p className={`text-gray60 ${TYPO.TYPO5_1_MD}`}>
              {!viewed ? t("goView") : t("viewAgain")}
            </p>
            <Forward fill="gray40" height={16} width={16} />
          </TrackedLink>
        )}
      </div>
      {type === "main" && (
        <BriefingFloating date={briefingDate} adVisible={adVisible} />
      )}
    </div>
  );
}

export const NewBriefingIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <rect x="2" y="4" width="20" height="16" rx="4" fill="#CFD8E1" />
      <path
        d="M11.5684 12.1158C11.7658 11.9651 12.0394 11.9641 12.2383 12.1129L20.9883 18.6598C20.2557 19.4818 19.1897 20.0006 18.002 20.0006H6.00195C4.80997 20.0006 3.74063 19.4782 3.00781 18.651L11.5684 12.1158Z"
        fill={TAILWIND_TO_CSS["gray5"]}
      />
      <path
        d="M18.2963 3.99951H5.7037C3.65821 3.99951 2 5.65772 2 7.70322L9.35812 14.1756C10.8687 15.5044 13.1313 15.5044 14.6419 14.1756L22 7.70322C22 5.65772 20.3418 3.99951 18.2963 3.99951Z"
        fill={TAILWIND_TO_CSS["gray40"]}
      />
      <path
        d="M18.3333 4H5.66667C3.64162 4 2 5.64162 2 7.66667L9.63455 13.2653C11.0425 14.2978 12.9575 14.2978 14.3655 13.2653L22 7.66667C22 5.64162 20.3584 4 18.3333 4Z"
        fill={TAILWIND_TO_CSS["gray5"]}
      />
      <circle cx="20.5" cy="4.5" r="2.5" fill="var(--red40)" />
    </svg>
  );
};
const CheckedIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M10.6 13.8001L8.45 11.6501C8.26667 11.4668 8.03333 11.3751 7.75 11.3751C7.46667 11.3751 7.23333 11.4668 7.05 11.6501C6.86667 11.8335 6.775 12.0668 6.775 12.3501C6.775 12.6335 6.86667 12.8668 7.05 13.0501L9.9 15.9001C10.1 16.1001 10.3333 16.2001 10.6 16.2001C10.8667 16.2001 11.1 16.1001 11.3 15.9001L16.95 10.2501C17.1333 10.0668 17.225 9.83346 17.225 9.55012C17.225 9.26679 17.1333 9.03346 16.95 8.85012C16.7667 8.66679 16.5333 8.57512 16.25 8.57512C15.9667 8.57512 15.7333 8.66679 15.55 8.85012L10.6 13.8001ZM12 22.0001C10.6167 22.0001 9.31667 21.7376 8.1 21.2126C6.88333 20.6876 5.825 19.9751 4.925 19.0751C4.025 18.1751 3.3125 17.1168 2.7875 15.9001C2.2625 14.6835 2 13.3835 2 12.0001C2 10.6168 2.2625 9.31679 2.7875 8.10012C3.3125 6.88346 4.025 5.82512 4.925 4.92512C5.825 4.02512 6.88333 3.31262 8.1 2.78762C9.31667 2.26262 10.6167 2.00012 12 2.00012C13.3833 2.00012 14.6833 2.26262 15.9 2.78762C17.1167 3.31262 18.175 4.02512 19.075 4.92512C19.975 5.82512 20.6875 6.88346 21.2125 8.10012C21.7375 9.31679 22 10.6168 22 12.0001C22 13.3835 21.7375 14.6835 21.2125 15.9001C20.6875 17.1168 19.975 18.1751 19.075 19.0751C18.175 19.9751 17.1167 20.6876 15.9 21.2126C14.6833 21.7376 13.3833 22.0001 12 22.0001Z"
        fill="var(--blue40)"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M8.44844 11.6531L10.5984 13.8031L15.5484 8.85312C15.7318 8.66979 15.9651 8.57812 16.2484 8.57812C16.5318 8.57812 16.7651 8.66979 16.9484 8.85312C17.1318 9.03646 17.2234 9.26979 17.2234 9.55313C17.2234 9.83646 17.1318 10.0698 16.9484 10.2531L11.2984 15.9031C11.0984 16.1031 10.8651 16.2031 10.5984 16.2031C10.3318 16.2031 10.0984 16.1031 9.89844 15.9031L7.04844 13.0531C6.8651 12.8698 6.77344 12.6365 6.77344 12.3531C6.77344 12.0698 6.8651 11.8365 7.04844 11.6531C7.23177 11.4698 7.4651 11.3781 7.74844 11.3781C8.03177 11.3781 8.2651 11.4698 8.44844 11.6531Z"
        fill="white"
      />
    </svg>
  );
};
export default BriefingAd;
